import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ConfigProvider, theme as antdTheme } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import App from './App';
import {
  ThemeModeContext,
  THEME_STORAGE_KEY,
  applyThemeMode,
  freezeThemeSwitchMotion,
  getInitialThemeMode,
  normalizeThemeMode,
} from './contexts/ThemeContext';
import {
  PerformanceModeContext,
  PERFORMANCE_STORAGE_KEY,
  applyPerformanceMode,
  getInitialPerformanceMode,
  normalizePerformanceMode,
  resolvePerformanceMode,
} from './contexts/PerformanceContext';
import { setupAntdPopupPositionFix } from './utils/antdPopupPositionFix';

import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';

import './index.css';

const RESIZE_OBSERVER_DEV_WARNINGS = [
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded',
];

const isResizeObserverDevWarning = (message) => (
  typeof message === 'string' &&
  RESIZE_OBSERVER_DEV_WARNINGS.some((warning) => message.includes(warning))
);

if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  window.addEventListener(
    'error',
    (event) => {
      if (isResizeObserverDevWarning(event.message)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  window.addEventListener(
    'unhandledrejection',
    (event) => {
      const reason = event.reason;
      const message = reason?.message || String(reason || '');
      if (isResizeObserverDevWarning(message)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );
}

// 设置 dayjs 中文
dayjs.locale('zh-cn');

const initialThemeMode = getInitialThemeMode();
applyThemeMode(initialThemeMode);
const initialPerformanceMode = getInitialPerformanceMode();
const initialEffectivePerformanceMode = applyPerformanceMode(initialPerformanceMode);
setupAntdPopupPositionFix();

const createAntdTheme = (mode, performanceMode = 'full') => {
  const isDark = mode === 'dark';
  const isSafe = performanceMode === 'safe';

  return {
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: isDark ? '#7bd7c6' : '#117f72',
      colorInfo: isDark ? '#9bb2f4' : '#3f67d7',
      colorSuccess: isDark ? '#69d6a6' : '#11946a',
      colorWarning: isDark ? '#e8bd65' : '#ad6c12',
      colorError: isDark ? '#e28a96' : '#ad3f4d',
      colorBgBase: isDark ? '#0d141e' : '#f3f7f6',
      colorTextBase: isDark ? '#e8eef3' : '#102231',
      colorBorder: isDark ? 'rgba(134, 162, 188, 0.20)' : 'rgba(43, 83, 94, 0.16)',
      colorBorderSecondary: isDark ? 'rgba(134, 162, 188, 0.13)' : 'rgba(43, 83, 94, 0.11)',
      borderRadius: 14,
      borderRadiusLG: 22,
      borderRadiusSM: 8,
      motion: !isSafe,
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif",
      boxShadow:
        isSafe
          ? (isDark ? '0 8px 20px rgba(4, 10, 18, 0.22)' : '0 8px 20px rgba(28, 74, 86, 0.07)')
          : isDark
          ? '0 22px 58px rgba(4, 10, 18, 0.40)'
          : '0 18px 42px rgba(28, 74, 86, 0.12)',
      controlHeight: 38,
      controlHeightLG: 44,
      controlHeightSM: 30,
    },
    components: {
      Button: {
        borderRadius: 14,
        fontWeight: 600,
        primaryShadow: isDark
          ? (isSafe ? '0 4px 12px rgba(123, 215, 198, 0.10)' : '0 12px 26px rgba(123, 215, 198, 0.16)')
          : (isSafe ? '0 4px 12px rgba(17, 127, 114, 0.10)' : '0 12px 24px rgba(17, 127, 114, 0.16)'),
      },
      Card: {
        borderRadiusLG: 22,
        headerBg: isDark ? 'rgba(24, 36, 51, 0.92)' : 'rgba(255, 255, 255, 0.92)',
      },
      Table: {
        headerBg: isDark ? 'rgba(29, 43, 60, 0.98)' : '#eef6f5',
        headerColor: isDark ? '#e8eef3' : '#102231',
        rowHoverBg: isDark ? 'rgba(123, 215, 198, 0.085)' : 'rgba(17, 127, 114, 0.065)',
        borderColor: isDark ? 'rgba(134, 162, 188, 0.13)' : 'rgba(43, 83, 94, 0.11)',
      },
      Modal: {
        contentBg: isDark ? '#1d2a3a' : '#ffffff',
        headerBg: isDark ? '#1d2a3a' : '#ffffff',
      },
      Input: {
        activeBorderColor: isDark ? '#7bd7c6' : '#117f72',
        hoverBorderColor: isDark ? '#9ce4d6' : '#0b6d62',
      },
      Select: {
        optionSelectedBg: isDark ? 'rgba(41, 132, 107, 0.22)' : '#e5f8f1',
      },
      DatePicker: {
        activeBorderColor: isDark ? '#7bd7c6' : '#117f72',
      },
      Pagination: {
        itemActiveBg: isDark ? 'rgba(123, 215, 198, 0.16)' : '#e5f8f1',
      },
      Tag: {
        borderRadiusSM: 999,
      },
    },
  };
};

const Root = () => {
  const [themeMode, setThemeModeState] = React.useState(initialThemeMode);
  const [performanceMode, setPerformanceModeState] = React.useState(initialPerformanceMode);
  const [effectivePerformanceMode, setEffectivePerformanceMode] = React.useState(initialEffectivePerformanceMode);

  React.useEffect(() => {
    applyThemeMode(themeMode);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, themeMode);
    } catch {
      // 忽略本地存储不可用的场景，主题仍可在当前页面生效
    }
  }, [themeMode]);

  React.useEffect(() => {
    const nextEffectiveMode = applyPerformanceMode(performanceMode);
    setEffectivePerformanceMode(nextEffectiveMode);
    try {
      window.localStorage.setItem(PERFORMANCE_STORAGE_KEY, performanceMode);
    } catch {
      // 忽略本地存储不可用的场景，性能模式仍可在当前页面生效
    }
  }, [performanceMode]);

  const themeValue = React.useMemo(() => {
    const setThemeMode = (mode) => {
      freezeThemeSwitchMotion();
      setThemeModeState(normalizeThemeMode(mode));
    };
    const toggleThemeMode = () => {
      freezeThemeSwitchMotion();
      setThemeModeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
    };

    return {
      themeMode,
      isDark: themeMode === 'dark',
      setThemeMode,
      toggleThemeMode,
    };
  }, [themeMode]);

  const performanceValue = React.useMemo(() => {
    const setPerformanceMode = (mode) => {
      const safeMode = normalizePerformanceMode(mode);
      setPerformanceModeState(safeMode);
      setEffectivePerformanceMode(resolvePerformanceMode(safeMode));
    };
    const togglePerformanceMode = () => {
      setPerformanceModeState((prev) => {
        const currentEffectiveMode = resolvePerformanceMode(prev);
        const nextMode = currentEffectiveMode === 'safe' ? 'full' : 'safe';
        setEffectivePerformanceMode(nextMode);
        return nextMode;
      });
    };

    return {
      performanceMode,
      effectivePerformanceMode,
      isPerformanceSafe: effectivePerformanceMode === 'safe',
      setPerformanceMode,
      togglePerformanceMode,
    };
  }, [performanceMode, effectivePerformanceMode]);

  const antdThemeConfig = React.useMemo(
    () => createAntdTheme(themeMode, effectivePerformanceMode),
    [themeMode, effectivePerformanceMode]
  );

  return (
    <ThemeModeContext.Provider value={themeValue}>
      <PerformanceModeContext.Provider value={performanceValue}>
        <ConfigProvider
          locale={zhCN}
          theme={antdThemeConfig}
          button={{ autoInsertSpace: false }}
          wave={{ disabled: effectivePerformanceMode === 'safe' }}
        >
          <App />
        </ConfigProvider>
      </PerformanceModeContext.Provider>
    </ThemeModeContext.Provider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <BrowserRouter>
    <Root />
  </BrowserRouter>
);
